import { Component, Inject, ContentChildren, QueryList, AfterContentInit, OnInit, OnDestroy } from '@angular/core';
import { Observable, merge, of, Subject } from 'rxjs';
import { Router, GuardsCheckEnd } from '@angular/router';
import { filter, map, share, tap } from 'rxjs/operators';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { IdentityService } from 'src/app/core/identity.service';
import { MenuService } from 'src/app/core/menu.service';
import { MenuItemDirective } from 'src/app/nav-menu/menu-item.directive';
import { MatDialog } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { CandidateSearchModalComponent } from '../recruiting/candidates/candidate-search/components/candidate-search-modal/candidate-search-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { selectUnreadInAppNotificationCounts } from 'src/app/in-app-notifications/store/selectors/in-app-notifications.selectors';
import { selectRecruitingDealAlertData } from '../root-store/recruiting-store/selectors/deals.selector';
import { UnreadInAppNotificationCounts } from 'src/app/in-app-notifications/models/unread-in-app-notification-counts.model';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { NavMenuLinkComponent } from 'src/app/nav-menu/nav-menu-link.component';
import { VersionComponent } from 'src/app/shared/components/version/version.component';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';

enum MODULES {
    WELCOME = 'welcome',
    REPORTING = 'reporting',
    CANDIDATE_SEARCH = '_recruiting',
    IN_APP_NOTIFICATIONS = 'in-app-notifications'
}

@UntilDestroy()
@Component({
    selector: 'nova-nav-menu',
    standalone: true,
    imports: [
        MatIconModule,
        MatListModule,
        MatButtonModule,
        AsyncPipe,
        NavMenuLinkComponent,
        VersionComponent,
        NgOptimizedImage
    ],
    templateUrl: './nav-menu.component.html',
    styleUrls: ['./_styles.scss', './nav-menu.component.scss']
})
export class NavMenuComponent implements OnInit, AfterContentInit, OnDestroy {
    isCollapsed = false;

    domain: string;

    userId = 0;

    aspNetUserId: string;

    organizationId = 0;

    intranetDomain: string;

    openedModule: string;

    activeModule: string;

    activeModuleItem: string;

    menuModules$: Observable<MenuItemDirective[]>;

    hidden = false;

    dealAlert = 0;

    @ContentChildren(MenuItemDirective)
    private readonly _menuItems: QueryList<MenuItemDirective>;

    readonly moduleTypes = MODULES;

    readonly openedModuleSessionStorageKey = 'openedModule';
    moduleIsClicked: string;

    private readonly _cleanup$ = new Subject();

    unreadMessagesCount = 0;

    get showInAppNotifications(): boolean {
        return this.aspNetUserId && this.isLoggedIn;
    }

    constructor(
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _identityService: IdentityService,
        private readonly _router: Router,
        private readonly _menuService: MenuService,
        public dialog: MatDialog,
        private readonly overlay: Overlay,
        private readonly _mediaObserver: BreakpointObserver,
        private readonly _store: Store
    ) {
        this.domain = this._settings.domain;
        this.organizationId = this._settings.organizationId;
        this.intranetDomain = this._settings.intranetDomain;
        //Show menu as hidden:true from the beggining to avoid flicker for a second with full screen login page.
        this.hidden = this._identityService.enforceOAuth;
    }

    ngOnInit(): void {
        this._mediaObserver
            .observe(['xs', 'md', 'sm', 'lt-md'])
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result.matches) {
                    this.isCollapsed = true;
                } else {
                    this.isCollapsed = false;
                }
            });

        this.menuModules$ = merge(
            of([]),
            this._router.events.pipe(
                filter((event) => event instanceof GuardsCheckEnd),
                tap((event: GuardsCheckEnd) => {
                    this.hidden = this._menuService.isHiddenMenuRoute(
                        event.urlAfterRedirects,
                        this._identityService.enforceOAuth
                    );
                }),
                map((event: GuardsCheckEnd) => {
                    this.userId = 0;
                    this.dealAlert = null;
                    const identity = this._identityService.current;

                    if (identity && !MenuService.publicRoutes.includes(event.url)) {
                        this.userId = identity.contactId;
                        this.aspNetUserId = identity.userId;
                        if (this.userId) {
                            this._store
                                .select(selectRecruitingDealAlertData)
                                .pipe(share(), untilDestroyed(this))
                                .subscribe((data) => {
                                    if (data) {
                                        if (data.deals && data.deals > 0 && data.recruiterId === this.userId) {
                                            this.dealAlert = data.deals;
                                        }
                                    }
                                });
                        }
                        if (this.aspNetUserId) {
                            this._store
                                .select(selectUnreadInAppNotificationCounts)
                                .pipe(share(), untilDestroyed(this))
                                .subscribe((unreadInAppNotificationCounts: UnreadInAppNotificationCounts) => {
                                    this.unreadMessagesCount = unreadInAppNotificationCounts.unreadNotificationCount;
                                    this.unreadMessagesCount +=
                                        unreadInAppNotificationCounts.overdueTasksAssignedToMeCount;
                                });
                        }
                        const item = this._menuService.urlToModuleItem(event.url);
                        this.activeModule = item.moduleName;
                        this.activeModuleItem = item.itemName;
                        this.openedModule = this.activeModuleItem
                            ? sessionStorage.getItem(this.openedModuleSessionStorageKey)
                            : null;
                        if (event.url.includes('/in-app-notifications')) {
                            this.moduleIsClicked = this.moduleTypes.IN_APP_NOTIFICATIONS;
                        } else {
                            this.moduleIsClicked = this.openedModule;
                        }

                        return this._menuService.getVisibleItems(identity);
                    }
                    return [];
                })
            )
        );
    }

    ngAfterContentInit(): void {
        this._menuItems.forEach((item) => this._menuService.register(item));
    }

    ngOnDestroy(): void {
        this._cleanup$.next(true);
        this._cleanup$.complete();
    }

    get isLoggedIn(): boolean {
        return this.userId > 0;
    }

    openModule(moduleName: string): void {
        this.moduleIsClicked = moduleName;
        if (moduleName === this.openedModule) {
            this.openedModule = null;
        } else {
            this.openedModule = moduleName;
        }
        sessionStorage.setItem(this.openedModuleSessionStorageKey, this.openedModule);
        if (moduleName === this.moduleTypes.WELCOME) {
            this._router.navigate(['/welcome/home']);
        } else if (moduleName === this.moduleTypes.REPORTING) {
            this._router.navigate(['/reporting/reports']);
        } else if (moduleName === this.moduleTypes.CANDIDATE_SEARCH) {
            this.openModal();
        } else if (moduleName === this.moduleTypes.IN_APP_NOTIFICATIONS) {
            this._router.navigate(['/in-app-notifications']);
        }
    }

    toggleSidebar(): void {
        this.isCollapsed = !this.isCollapsed;
    }

    openModal() {
        const scrollStrategy = this.overlay.scrollStrategies.reposition();
        this.dialog.open(CandidateSearchModalComponent, {
            position: {
                left: '250px'
            },
            maxWidth: '100%',
            panelClass: ['search-modal-panel', 'nova__dialog'],
            scrollStrategy
        });
    }
}
